import { TickCircle } from "iconsax-react";
import { Copy } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCrefazClt } from "@/app/contexts/CrefazCltContext";
import { crefazCltService } from "@/app/services/crefazCltService";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";

interface SentProposalProps {
  visible?: boolean;
  contract: string;
}

export default function SentProposal({ visible, contract }: SentProposalProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { customerData, preTransaction } = useCrefazClt();

  const [signatureUrl, setSignatureUrl] = useState("");
  const [isLoadingLink, setIsLoadingLink] = useState(false);

  const handleGetSignatureLink = async () => {
    if (!preTransaction?.id) return;

    try {
      setIsLoadingLink(true);

      const { url } = await crefazCltService.getSignatureLink(
        preTransaction.id,
      );

      if (!url) {
        toast({
          title: "Link ainda não disponível",
          description:
            "A Crefaz libera a assinatura quando a proposta entra em formalização.",
        });
        return;
      }

      setSignatureUrl(url);
    } catch (error: any) {
      toast({
        title: "Não foi possível obter o link de assinatura",
        description: error?.message || "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (!signatureUrl) return;

    await navigator.clipboard.writeText(signatureUrl);

    toast({ title: "Link copiado com sucesso" });
  };

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="flex flex-col items-start gap-1.5">
        <TickCircle variant="Bold" size={40} className="text-success" />
        <strong className="font-semibold">Proposta enviada!</strong>
        <p className="text-sm font-medium text-black04">
          A proposta foi registrada na Crefaz. Acompanhe o status pelo seu
          dashboard.
        </p>
      </div>

      <div className="flex flex-col items-start gap-1.5">
        <p className="text-sm font-medium">Contrato</p>
        <h3 className="text-3xl font-semibold">{contract}</h3>
        <p className="text-sm font-medium text-black04">
          Para{" "}
          <strong className="font-semibold text-black01">
            {customerData?.name}
          </strong>
        </p>
      </div>

      {signatureUrl ? (
        <div className="flex items-center gap-2 rounded-2xl border border-gray01 p-4">
          <Copy
            className="cursor-pointer transition-opacity hover:opacity-70"
            size={18}
            onClick={handleCopyLink}
          />
          <p className="break-all text-sm text-black01">{signatureUrl}</p>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="rounded-full font-semibold text-primary"
          onClick={handleGetSignatureLink}
          isLoading={isLoadingLink}
          disabled={isLoadingLink}
        >
          Gerar link de assinatura
        </Button>
      )}

      <div className="flex justify-end">
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={() => navigate("/transacoes")}
        >
          Concluir
        </Button>
      </div>
    </div>
  );
}
